'use client';

import { useEffect, useRef } from 'react';

interface TurnstileWidgetProps {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  action?: string;
  className?: string;
}

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          callback?: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          action?: string;
          appearance?: 'always' | 'execute' | 'interaction-only';
        },
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId?: string) => void;
    };
    onloadTurnstileCallback?: () => void;
  }
}

const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback&render=explicit';

/**
 * Widget de Cloudflare Turnstile. Solo se monta si está configurado el
 * NEXT_PUBLIC_TURNSTILE_SITE_KEY. En caso contrario, no renderiza nada y
 * el formulario sigue funcionando sin captcha (modo dev).
 */
export function TurnstileWidget({
  onVerify,
  onExpire,
  theme = 'dark',
  action,
  className,
}: TurnstileWidgetProps) {
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
  const ref = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!siteKey || !ref.current) return;

    function render() {
      if (!window.turnstile || !ref.current) return;
      if (widgetIdRef.current) {
        try {
          window.turnstile.reset(widgetIdRef.current);
        } catch {
          // ignore
        }
        return;
      }
      try {
        widgetIdRef.current = window.turnstile.render(ref.current, {
          sitekey: siteKey!,
          callback: onVerify,
          'expired-callback': () => {
            onExpire?.();
          },
          theme,
          action,
          appearance: 'interaction-only',
        });
      } catch (err) {
        // No bloqueamos el flow si Turnstile falla
        console.warn('Turnstile render failed', err);
      }
    }

    if (window.turnstile) {
      render();
    } else {
      window.onloadTurnstileCallback = render;
      if (!document.getElementById(SCRIPT_ID)) {
        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
    }

    const id = widgetIdRef.current;
    return () => {
      if (id && window.turnstile) {
        try {
          window.turnstile.remove(id);
        } catch {
          // ignore
        }
      }
      widgetIdRef.current = null;
    };
  }, [siteKey, onVerify, onExpire, theme, action]);

  if (!siteKey) return null;

  return <div ref={ref} className={className} />;
}
